import React, { createContext, useContext, useState, useEffect, useRef, ReactNode, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { supabase } from '../lib/supabase';
import { InventoryItem, Shipment } from '../types';
import { generateUniqueBatchNumber } from '../utils/batchNumber';
import { getAdminId as resolveAdminId } from '../utils/getAdminId';
import { createContextLogger } from '../lib/logger';
import { SESSION_END_EVENT } from '../lib/tenantStorage';
import { checkProductDeletion, DeletionBlocker } from '../lib/product-deletion';

const log = createContextLogger('InventoryContext');

export interface DeleteItemResult {
  success: boolean;
  /** Why the delete was refused, ready to show to the user. */
  reason?: string;
  blockers?: DeletionBlocker[];
}

export interface InventoryFilters {
  searchTerm?: string;
  category?: string;
  locationId?: string;
  colorId?: string;
  sizeId?: string;
  stockFilter?: 'all' | 'in_stock' | 'low_stock' | 'out_of_stock';
  sortColumn?: string;
  sortDirection?: 'asc' | 'desc';
}

export interface PaginationState {
  currentPage: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
}

interface InventoryContextType {
  items: InventoryItem[];
  allItems: InventoryItem[];
  allItemsLoading: boolean;
  shipments: Shipment[];
  loading: boolean;
  error: string | null;
  pagination: PaginationState;
  filters: InventoryFilters;
  addItem: (item: Omit<InventoryItem, 'id' | 'user_id' | 'created_at' | 'updated_at'>) => Promise<InventoryItem | null>;
  updateItem: (id: string, updates: Partial<InventoryItem>) => Promise<InventoryItem | null>;
  deleteItem: (id: string) => Promise<DeleteItemResult>;
  addShipment: (shipment: any, items: any[]) => Promise<any>;
  refreshInventory: () => Promise<void>;
  updateMultipleItems: (itemIds: string[], updates: Partial<InventoryItem>) => Promise<{ success: string[]; failed: string[] }>;
  loadPage: (page: number, pageSize?: number, filters?: InventoryFilters) => Promise<void>;
  setFilters: (filters: InventoryFilters) => void;
  getTotalCount: (filters?: InventoryFilters) => Promise<number>;
  fetchAllItems: (forceRefresh?: boolean) => Promise<InventoryItem[]>;
  invalidateAllItemsCache: () => void;
  isLoading?: boolean;
}

const InventoryContext = createContext<InventoryContextType | undefined>(undefined);

interface InventoryProviderProps {
  children: ReactNode;
}

const ALL_ITEMS_CACHE_DURATION = 60000;

export const InventoryProvider: React.FC<InventoryProviderProps> = ({ children }) => {
  const { user, buyer } = useAuth();
  const [items, setItems] = useState<InventoryItem[]>([]);
  const [allItems, setAllItems] = useState<InventoryItem[]>([]);
  const [allItemsLoading, setAllItemsLoading] = useState(false);
  const [allItemsLastFetched, setAllItemsLastFetched] = useState<number>(0);
  const cachedAdminIdRef = useRef<string | null>(null);
  const fetchGenerationRef = useRef(0);
  const [shipments, setShipments] = useState<Shipment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<PaginationState>({
    currentPage: 1,
    pageSize: 25,
    totalItems: 0,
    totalPages: 0
  });
  const [filters, setFiltersState] = useState<InventoryFilters>({});

  const getAdminId = useCallback(() => {
    if (user) {
      if (user.role === 'admin' || user.role === 'worker' || user.role === 'viewer') {
        return resolveAdminId(user);
      }
    }
    if (buyer) return buyer.admin_id;
    return null;
  }, [user, buyer]);

  useEffect(() => {
    const adminId = getAdminId();
    if (adminId === cachedAdminIdRef.current) return;
    cachedAdminIdRef.current = adminId;
    setItems([]);
    setAllItems([]);
    setShipments([]);
    setAllItemsLastFetched(0);
  }, [user?.id, buyer?.id, getAdminId]);

  useEffect(() => {
    function handleSessionEnd() {
      cachedAdminIdRef.current = null;
      setItems([]);
      setAllItems([]);
      setShipments([]);
      setAllItemsLastFetched(0);
    }
    window.addEventListener(SESSION_END_EVENT, handleSessionEnd);
    return () => window.removeEventListener(SESSION_END_EVENT, handleSessionEnd);
  }, []);

  const buildFilteredQuery = useCallback((baseQuery: any, adminId: string | null, filterParams: InventoryFilters, skipSearch: boolean = false) => {
    let query = baseQuery;

    if (adminId) {
      query = query.eq('admin_id', adminId);
    }

    if (filterParams.category) {
      query = query.eq('category', filterParams.category);
    }

    if (filterParams.colorId) {
      query = query.eq('color_id', filterParams.colorId);
    }

    if (filterParams.sizeId) {
      query = query.eq('size_id', filterParams.sizeId);
    }

    if (filterParams.stockFilter && filterParams.stockFilter !== 'all') {
      switch (filterParams.stockFilter) {
        case 'out_of_stock':
          query = query.eq('quantity', 0);
          break;
        case 'low_stock':
          query = query.gt('quantity', 0).lte('quantity', 10);
          break;
        case 'in_stock':
          query = query.gt('quantity', 10);
          break;
      }
    }

    if (!skipSearch && filterParams.searchTerm) {
      const term = filterParams.searchTerm.trim().toLowerCase();
      query = query.or(`name.ilike.%${term}%,sku.ilike.%${term}%,barcode.ilike.%${term}%,reference_code.ilike.%${term}%`);
    }

    return query;
  }, []);

  const getTotalCount = useCallback(async (filterParams?: InventoryFilters): Promise<number> => {
    const adminId = getAdminId();
    if (!adminId) return 0;
    const activeFilters = filterParams || filters;

    try {
      if (activeFilters.searchTerm && activeFilters.searchTerm.trim()) {
        const { data, error: rpcError } = await supabase.rpc('get_products_count_v2', {
          p_admin_id: adminId,
          p_search_term: activeFilters.searchTerm.trim(),
          p_category: activeFilters.category || null,
          p_color_id: activeFilters.colorId || null,
          p_size_id: activeFilters.sizeId || null,
          p_stock_status: activeFilters.stockFilter || 'all'
        });
        if (rpcError) {
          log.error('Error getting count via RPC', { error: rpcError.message });
          return 0;
        }
        return data || 0;
      }

      let query = supabase.from('products').select('id', { count: 'exact', head: true });
      query = buildFilteredQuery(query, adminId, activeFilters, true);

      const { count, error: countError } = await query;
      if (countError) {
        log.error('Error getting count', { error: countError.message });
        return 0;
      }
      return count || 0;
    } catch (err) {
      log.error('Error in getTotalCount', { error: String(err) });
      return 0;
    }
  }, [getAdminId, filters, buildFilteredQuery]);

  const mapProductToInventoryItem = useCallback((p: any): InventoryItem => ({
    id: p.id,
    user_id: p.admin_id,
    name: p.name,
    description: p.description,
    sku: p.sku,
    barcode: p.barcode,
    batch: p.barcode || p.sku,
    batch_number: p.batch_number,
    reference_code: p.reference_code,
    category: p.category,
    quantity: p.quantity,
    unit_price: p.unit_price || p.sell_price,
    buy_price: p.purchase_price || p.cost_price,
    sell_price: p.sell_price || p.unit_price,
    supplier: p.supplier,
    location: p.location,
    minimum_stock: p.min_stock_level,
    maximum_stock: p.max_stock_level,
    reorder_point: p.reorder_point,
    status: p.status,
    image_url: p.image_url,
    color_id: p.color_id,
    size_id: p.size_id,
    weight: p.weight,
    weight_unit: p.weight_unit,
    created_at: p.created_at,
    updated_at: p.updated_at
  }), []);

  const loadPage = useCallback(async (page: number, pageSize?: number, filterParams?: InventoryFilters) => {
    if (!user) return;

    const generation = ++fetchGenerationRef.current;
    setLoading(true);
    setError(null);

    try {
      const adminId = getAdminId();
      if (!adminId) {
        if (generation === fetchGenerationRef.current) setLoading(false);
        return;
      }
      const activeFilters = filterParams || filters;
      const activPageSize = pageSize || pagination.pageSize;

      const sortCol = activeFilters.sortColumn || 'created_at';
      const sortDir = activeFilters.sortDirection || 'desc';

      const dbSortColumn = sortCol === 'buy_price' ? 'purchase_price' :
                           sortCol === 'sell_price' ? 'sell_price' :
                           sortCol === 'minimum_stock' ? 'min_stock_level' :
                           sortCol === 'color' ? 'color_id' :
                           sortCol === 'size' ? 'size_id' : sortCol;

      if (activeFilters.searchTerm && activeFilters.searchTerm.trim()) {
        const offset = (Math.max(1, page) - 1) * activPageSize;
        const { data: rpcResult, error: rpcError } = await supabase.rpc('search_products_v2', {
          p_admin_id: adminId,
          p_search_term: activeFilters.searchTerm.trim(),
          p_category: activeFilters.category || null,
          p_color_id: activeFilters.colorId || null,
          p_size_id: activeFilters.sizeId || null,
          p_stock_status: activeFilters.stockFilter || 'all',
          p_sort_column: dbSortColumn,
          p_sort_direction: sortDir,
          p_limit: activPageSize,
          p_offset: offset
        });

        if (rpcError) throw rpcError;
        if (generation !== fetchGenerationRef.current) return;

        const totalItems = rpcResult?.total_count || 0;
        const totalPages = Math.ceil(totalItems / activPageSize);
        const validPage = Math.min(Math.max(1, page), Math.max(1, totalPages));
        const products = rpcResult?.products || [];

        setItems(products.map(mapProductToInventoryItem));
        setPagination({
          currentPage: validPage,
          pageSize: activPageSize,
          totalItems,
          totalPages
        });
        if (filterParams) {
          setFiltersState(filterParams);
        }
        return;
      }

      const totalItems = await getTotalCount(activeFilters);
      const totalPages = Math.ceil(totalItems / activPageSize);
      const validPage = Math.min(Math.max(1, page), Math.max(1, totalPages));

      const from = (validPage - 1) * activPageSize;
      const to = from + activPageSize - 1;

      let query = supabase.from('products').select('*');
      query = buildFilteredQuery(query, adminId, activeFilters, true);

      query = query.order(dbSortColumn, { ascending: sortDir === 'asc' });
      query = query.range(from, to);

      const { data: products, error: productsError } = await query;

      if (productsError) throw productsError;

      if (generation !== fetchGenerationRef.current) return;

      setItems((products || []).map(mapProductToInventoryItem));
      setPagination({
        currentPage: validPage,
        pageSize: activPageSize,
        totalItems,
        totalPages
      });
      if (filterParams) {
        setFiltersState(filterParams);
      }
    } catch (error) {
      if (generation !== fetchGenerationRef.current) return;
      log.error('Failed to load inventory page', { error: String(error) });
      setError(error instanceof Error ? error.message : 'Failed to load inventory');
      setItems([]);
    } finally {
      if (generation === fetchGenerationRef.current) setLoading(false);
    }
  }, [user, getAdminId, filters, pagination.pageSize, getTotalCount, buildFilteredQuery, mapProductToInventoryItem]);

  const setFilters = useCallback((newFilters: InventoryFilters) => {
    setFiltersState(newFilters);
    loadPage(1, pagination.pageSize, newFilters);
  }, [loadPage, pagination.pageSize]);

  useEffect(() => {
    if (user) {
      loadPage(1);
    }
  }, [user]);

  const loadInventory = async () => {
    await loadPage(pagination.currentPage, pagination.pageSize, filters);
  };

  const addItem = async (itemData: Omit<InventoryItem, 'id' | 'user_id' | 'created_at' | 'updated_at'>): Promise<InventoryItem | null> => {
    if (!user) return null;

    try {
      setLoading(true);

      if (user.role === 'super_admin') {
        log.error('Super admin cannot add products without specifying an admin');
        throw new Error('Super admin must specify an admin_id to add products');
      }
      const adminId = resolveAdminId(user);

      if (!adminId) {
        throw new Error('Unable to determine admin_id for product creation');
      }

      // Generate SKU if not provided
      const generateSKU = () => {
        const now = new Date();
        const year = now.getFullYear().toString().slice(-2);
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        const random = Math.floor(Math.random() * 9999).toString().padStart(4, '0');
        return `SKU${year}${month}${day}-${random}`;
      };

      const sku = itemData.sku || itemData.barcode || itemData.batch || generateSKU();

      const { data: product, error: insertError } = await supabase
        .from('products')
        .insert({
          admin_id: adminId,
          name: itemData.name,
          description: itemData.description,
          sku: sku,
          barcode: itemData.barcode || sku,
          batch_number: itemData.batch_number,
          reference_code: itemData.reference_code,
          category: itemData.category,
          quantity: itemData.quantity || 0,
          unit_price: itemData.unit_price || itemData.sell_price || 0,
          sell_price: itemData.sell_price,
          cost_price: itemData.buy_price || itemData.unit_price || 0,
          purchase_price: itemData.buy_price,
          min_stock_level: itemData.minimum_stock || 0,
          max_stock_level: itemData.maximum_stock,
          supplier: itemData.supplier,
          location: itemData.location,
          reorder_point: itemData.reorder_point,
          status: itemData.status || 'active',
          image_url: itemData.image_url,
          color_id: itemData.color_id || null,
          size_id: itemData.size_id || null,
          weight: itemData.weight ?? null,
          weight_unit: itemData.weight_unit ?? 'g'
        })
        .select()
        .single();

      if (insertError) throw insertError;

      const newItem: InventoryItem = {
        id: product.id,
        user_id: product.admin_id,
        name: product.name,
        description: product.description,
        sku: product.sku,
        barcode: product.barcode,
        batch: product.barcode || product.sku,
        batch_number: product.batch_number,
        reference_code: product.reference_code,
        category: product.category,
        quantity: product.quantity,
        unit_price: product.unit_price || product.sell_price,
        buy_price: product.purchase_price || product.cost_price,
        sell_price: product.sell_price || product.unit_price,
        supplier: product.supplier,
        location: product.location,
        minimum_stock: product.min_stock_level,
        maximum_stock: product.max_stock_level,
        reorder_point: product.reorder_point,
        status: product.status,
        image_url: product.image_url,
        color_id: product.color_id,
        size_id: product.size_id,
        weight: product.weight,
        weight_unit: product.weight_unit,
        created_at: product.created_at,
        updated_at: product.updated_at
      };

      setItems(prev => [newItem, ...prev]);
      setAllItems(prev => [newItem, ...prev]);
      setAllItemsLastFetched(0);
      log.info('Item added successfully', { name: newItem.name });

      return newItem;
    } catch (error) {
      log.error('Failed to add item', { error: String(error) });
      setError(error instanceof Error ? error.message : 'Failed to add item');
      return null;
    } finally {
      setLoading(false);
    }
  };

  const updateItem = async (itemId: string, updates: Partial<InventoryItem>): Promise<InventoryItem | null> => {
    if (!user) return null;

    try {
      setLoading(true);

      const updateData: any = {};
      if (updates.name !== undefined) updateData.name = updates.name;
      if (updates.description !== undefined) updateData.description = updates.description;
      if (updates.sku !== undefined) updateData.sku = updates.sku;
      if (updates.barcode !== undefined) updateData.barcode = updates.barcode;
      if (updates.batch_number !== undefined) updateData.batch_number = updates.batch_number;
      if (updates.reference_code !== undefined) updateData.reference_code = updates.reference_code;
      if (updates.category !== undefined) updateData.category = updates.category;
      if (updates.quantity !== undefined) updateData.quantity = updates.quantity;
      if (updates.unit_price !== undefined) updateData.unit_price = updates.unit_price;
      if (updates.buy_price !== undefined) {
        updateData.purchase_price = updates.buy_price;
        updateData.cost_price = updates.buy_price; // Keep legacy column in sync
      }
      if (updates.sell_price !== undefined) {
        updateData.sell_price = updates.sell_price;
        updateData.unit_price = updates.sell_price; // Keep legacy column in sync
      }
      if (updates.supplier !== undefined) updateData.supplier = updates.supplier;
      if (updates.location !== undefined) updateData.location = updates.location;
      if (updates.minimum_stock !== undefined) updateData.min_stock_level = updates.minimum_stock;
      if (updates.maximum_stock !== undefined) updateData.max_stock_level = updates.maximum_stock;
      if (updates.reorder_point !== undefined) updateData.reorder_point = updates.reorder_point;
      if (updates.status !== undefined) updateData.status = updates.status;
      if (updates.image_url !== undefined) updateData.image_url = updates.image_url;
      if (updates.color_id !== undefined) updateData.color_id = updates.color_id || null;
      if (updates.size_id !== undefined) updateData.size_id = updates.size_id || null;
      if (updates.weight !== undefined) updateData.weight = updates.weight;
      if (updates.weight_unit !== undefined) updateData.weight_unit = updates.weight_unit;

      updateData.updated_at = new Date().toISOString();

      const adminId = getAdminId();
      if (!adminId) throw new Error('No admin context');

      const { data: product, error: updateError } = await supabase
        .from('products')
        .update(updateData)
        .eq('id', itemId)
        .eq('admin_id', adminId)
        .select()
        .single();

      if (updateError) throw updateError;

      const updatedItem: InventoryItem = {
        id: product.id,
        user_id: product.admin_id,
        name: product.name,
        description: product.description,
        sku: product.sku,
        barcode: product.barcode,
        batch_number: product.batch_number,
        reference_code: product.reference_code,
        category: product.category,
        quantity: product.quantity,
        unit_price: product.unit_price || product.sell_price,
        buy_price: product.purchase_price || product.cost_price,
        sell_price: product.sell_price || product.unit_price,
        supplier: product.supplier,
        location: product.location,
        minimum_stock: product.min_stock_level,
        maximum_stock: product.max_stock_level,
        reorder_point: product.reorder_point,
        status: product.status,
        image_url: product.image_url,
        color_id: product.color_id,
        size_id: product.size_id,
        weight: product.weight,
        weight_unit: product.weight_unit,
        created_at: product.created_at,
        updated_at: product.updated_at
      };

      setItems(prev => prev.map(item =>
        item.id === itemId ? updatedItem : item
      ));
      setAllItems(prev => prev.map(item =>
        item.id === itemId ? updatedItem : item
      ));
      log.info('Item updated successfully', { name: updatedItem.name });

      return updatedItem;
    } catch (error) {
      log.error('Failed to update item', { error: String(error) });
      setError(error instanceof Error ? error.message : 'Failed to update item');
      return null;
    } finally {
      setLoading(false);
    }
  };

  const deleteItem = async (itemId: string): Promise<DeleteItemResult> => {
    if (!user) return { success: false, reason: 'You are not signed in.' };

    const adminId = getAdminId();
    if (!adminId) return { success: false, reason: 'No active company selected.' };

    try {
      setLoading(true);

      // Sales, invoices, reservations and shipments hold the product behind
      // NO ACTION foreign keys. Report them rather than deleting them to force
      // the removal through — a duplicate should be merged, not have its
      // history erased.
      const check = await checkProductDeletion({ productId: itemId, adminId });
      if (!check.canDelete) {
        log.info('Delete refused: product still has history', {
          blockerCount: check.blockers.length,
        });
        return { success: false, reason: check.message, blockers: check.blockers };
      }

      // Remaining references (per-location inventory, bin inventory, Shopify
      // maps, favourites) cascade or null out in the database.
      const { error: deleteError } = await supabase
        .from('products')
        .delete()
        .eq('id', itemId)
        .eq('admin_id', adminId);

      if (deleteError) throw deleteError;

      setItems(prev => prev.filter(item => item.id !== itemId));
      setAllItems(prev => prev.filter(item => item.id !== itemId));
      log.info('Item deleted successfully');

      return { success: true };
    } catch (error) {
      const reason = error instanceof Error ? error.message : 'Failed to delete item';
      log.error('Failed to delete item', { error: String(error) });
      setError(reason);
      return { success: false, reason };
    } finally {
      setLoading(false);
    }
  };

  const addShipment = async (shipmentData: any, items: any[]): Promise<any> => {
    if (!user) return null;

    try {
      const adminId = resolveAdminId(user);

      const { data: shipment, error: shipmentError } = await supabase
        .from('shipments')
        .insert({
          admin_id: adminId,
          shipment_number: shipmentData.shipment_number,
          shipment_type: shipmentData.type || 'incoming',
          supplier_name: shipmentData.supplier_name,
          customer_name: shipmentData.customer_name,
          shipping_address: shipmentData.shipping_address,
          expected_date: shipmentData.expected_date,
          status: shipmentData.status || 'pending',
          notes: shipmentData.notes,
          created_by: user.id
        })
        .select()
        .single();

      if (shipmentError) throw shipmentError;

      if (items && items.length > 0) {
        const shipmentItems = items.map(item => ({
          shipment_id: shipment.id,
          product_id: item.product_id,
          expected_quantity: item.expected_quantity,
          quantity: item.quantity
        }));

        const { error: itemsError } = await supabase
          .from('shipment_items')
          .insert(shipmentItems);

        if (itemsError) throw itemsError;
      }

      log.info('Shipment added successfully');
      return shipment;
    } catch (error) {
      log.error('Failed to add shipment', { error: String(error) });
      return null;
    }
  };

  const updateMultipleItems = async (
    itemIds: string[],
    updates: Partial<InventoryItem>
  ): Promise<{ success: string[]; failed: string[] }> => {
    if (!user) return { success: [], failed: itemIds };

    const success: string[] = [];
    const failed: string[] = [];

    for (const itemId of itemIds) {
      try {
        const result = await updateItem(itemId, updates);
        if (result) {
          success.push(itemId);
        } else {
          failed.push(itemId);
        }
      } catch (error) {
        log.error('Failed to update item', { itemId, error: String(error) });
        failed.push(itemId);
      }
    }

    return { success, failed };
  };

  const refreshInventory = async () => {
    await loadInventory();
  };

  const fetchAllItems = useCallback(async (forceRefresh = false): Promise<InventoryItem[]> => {
    if (!user && !buyer) return [];

    const adminId = getAdminId();
    if (!adminId) return [];

    const now = Date.now();
    const cacheValid =
      allItems.length > 0 &&
      cachedAdminIdRef.current === adminId &&
      (now - allItemsLastFetched) < ALL_ITEMS_CACHE_DURATION;

    if (!forceRefresh && cacheValid) {
      return allItems;
    }

    setAllItemsLoading(true);

    try {
      if (!adminId) {
        log.error('Unable to determine admin_id for fetching all items');
        return [];
      }

      const allFetchedItems: InventoryItem[] = [];
      const pageSize = 1000;
      let page = 0;
      let hasMore = true;

      while (hasMore) {
        const from = page * pageSize;
        const to = from + pageSize - 1;

        const { data: products, error: fetchError } = await supabase
          .from('products')
          .select('*')
          .eq('admin_id', adminId)
          .order('name', { ascending: true })
          .range(from, to);

        if (fetchError) {
          log.error('Error fetching all items', { error: fetchError.message });
          throw fetchError;
        }

        if (products && products.length > 0) {
          const mappedItems = products.map(mapProductToInventoryItem);
          allFetchedItems.push(...mappedItems);
          hasMore = products.length === pageSize;
          page++;
        } else {
          hasMore = false;
        }
      }

      setAllItems(allFetchedItems);
      setAllItemsLastFetched(now);

      return allFetchedItems;
    } catch (err) {
      log.error('Failed to fetch all items', { error: String(err) });
      setError(err instanceof Error ? err.message : 'Failed to fetch all inventory items');
      return allItems;
    } finally {
      setAllItemsLoading(false);
    }
  }, [user, buyer, getAdminId, allItems, allItemsLastFetched, mapProductToInventoryItem]);

  const invalidateAllItemsCache = useCallback(() => {
    setAllItemsLastFetched(0);
  }, []);

  return (
    <InventoryContext.Provider value={{
      items,
      allItems,
      allItemsLoading,
      shipments,
      loading,
      error,
      pagination,
      filters,
      addItem,
      updateItem,
      deleteItem,
      addShipment,
      refreshInventory,
      updateMultipleItems,
      loadPage,
      setFilters,
      getTotalCount,
      fetchAllItems,
      invalidateAllItemsCache,
      isLoading: loading
    }}>
      {children}
    </InventoryContext.Provider>
  );
};

export const useInventory = () => {
  const context = useContext(InventoryContext);
  if (!context) {
    throw new Error('useInventory must be used within an InventoryProvider');
  }
  return context;
};
